/**
 * Issue #428: Swimlane Cross-Column Edge
 * Custom edge for swimlane layout with Y-offset routing and sequence numbering
 * Issue #434: Added hover effects to indicate clickability
 * Issue #436: Improved click targets - wider interaction area and clickable labels
 */
import { memo, useMemo, useState, useCallback, type CSSProperties, type MouseEvent } from 'react';
import {
  EdgeProps,
  getBezierPath,
  getSmoothStepPath,
  EdgeLabelRenderer,
  useViewport,
  Position,
  useReactFlow,
} from '@xyflow/react';

/**
 * Data interface for swimlane cross-column edges
 */
interface SwimlaneCrossColumnEdgeData {
  /** Sequence number for chronological ordering */
  sequenceNumber: number;
  /** Suffix for parallel operations (a, b, c...) */
  parallelSuffix?: string;
  /** Y-offset for non-overlapping routing */
  yOffset: number;
  /** Bezier curvature for edge path */
  curvature: number;
  /** Column transition type for styling */
  transitionType: string;
  /** Whether this is an async operation */
  isAsync?: boolean;
}

/**
 * Configuration for edge rendering
 * Issue #436: Increased interaction area for easier clicking
 */
const CONFIG = {
  minZoomToShowLabel: 0.3,
  minZoomToShowBadge: 0.25,
  badgeSize: 24,
  labelFontSize: 11,
  // Issue #436: Wider interaction area for easier clicking
  interactionStrokeWidth: 40,
  // Issue #436: Clickable area around labels
  labelClickPadding: 12,
};

/**
 * Swimlane Cross-Column Edge Component
 * Features:
 * - Y-offset path calculation for non-overlapping routing
 * - Sequence number badge display
 * - Transition label with type indicator
 */
export const SwimlaneCrossColumnEdge = memo(function SwimlaneCrossColumnEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  label,
  labelStyle,
  style,
  markerEnd,
  data,
}: EdgeProps) {
  const { zoom } = useViewport();
  const { getNodes } = useReactFlow();
  const edgeData = data as SwimlaneCrossColumnEdgeData | undefined;

  // Issue #434: Hover state for visual feedback
  const [isHovered, setIsHovered] = useState(false);
  // Issue #436: Separate hover state for labels
  const [isLabelHovered, setIsLabelHovered] = useState(false);

  // Issue #436: Handle click on edge or label - dispatch edge click event
  const handleEdgeClick = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    // Find the ReactFlow instance and dispatch edge click
    const edge = { id, source, target, data, label, style, markerEnd };
    const nodes = getNodes();
    const sourceNode = nodes.find(n => n.id === source);
    const targetNode = nodes.find(n => n.id === target);

    // Dispatch custom event that E2EFlowRenderer can catch
    const event = new CustomEvent('edge-label-click', {
      bubbles: true,
      detail: { edge, sourceNode, targetNode }
    });
    (e.target as HTMLElement).dispatchEvent(event);
  }, [id, source, target, data, label, style, markerEnd, getNodes]);

  // Apply Y-offset to create parallel edge routing
  const yOffset = edgeData?.yOffset || 0;
  const offsetSourceY = sourceY + yOffset;
  const offsetTargetY = targetY + yOffset;

  // Calculate bezier path with custom curvature
  const curvature = edgeData?.curvature || 0.4;
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY: offsetSourceY,
    sourcePosition: sourcePosition || Position.Right,
    targetX,
    targetY: offsetTargetY,
    targetPosition: targetPosition || Position.Left,
    curvature,
  });

  // Visibility based on zoom
  const showBadge = zoom >= CONFIG.minZoomToShowBadge;
  const showLabel = label && zoom >= CONFIG.minZoomToShowLabel;

  // Format sequence number with optional suffix
  const sequenceText = useMemo(() => {
    if (!edgeData?.sequenceNumber) return '';
    return edgeData.parallelSuffix
      ? `${edgeData.sequenceNumber}${edgeData.parallelSuffix}`
      : `${edgeData.sequenceNumber}`;
  }, [edgeData?.sequenceNumber, edgeData?.parallelSuffix]);

  // Extract label text without sequence prefix (if already included)
  const labelText = useMemo(() => {
    if (!label) return '';
    const text = String(label);
    // Remove sequence prefix if present (e.g., "1: HTTP" -> "HTTP")
    return text.replace(/^\d+[a-z]?:\s*/, '');
  }, [label]);

  // Issue #436: Combined hover state for any part of the edge
  const isAnyHovered = isHovered || isLabelHovered;

  // Badge style with edge color - Issue #436: Made clickable with hover effects
  const badgeStyle: CSSProperties = useMemo(() => ({
    position: 'absolute',
    width: CONFIG.badgeSize,
    height: CONFIG.badgeSize,
    borderRadius: '50%',
    backgroundColor: style?.stroke as string || '#3b82f6',
    color: 'white',
    fontSize: 12,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: isAnyHovered ? '0 4px 8px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.2)',
    zIndex: 1001,
    border: '2px solid white',
    cursor: 'pointer',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    transform: isAnyHovered
      ? `translate(-50%, -50%) translate(${labelX}px, ${labelY - 14}px) scale(1.1)`
      : `translate(-50%, -50%) translate(${labelX}px, ${labelY - 14}px)`,
    pointerEvents: 'all',
  }), [labelX, labelY, style?.stroke, isAnyHovered]);

  // Label container style - Issue #436: Made clickable with hover effects
  const labelContainerStyle: CSSProperties = useMemo(() => ({
    position: 'absolute',
    transform: isAnyHovered
      ? `translate(-50%, -50%) translate(${labelX}px, ${labelY + 10}px) scale(1.05)`
      : `translate(-50%, -50%) translate(${labelX}px, ${labelY + 10}px)`,
    fontSize: CONFIG.labelFontSize,
    fontWeight: 600,
    background: isAnyHovered ? '#f0f9ff' : 'white',
    padding: `${CONFIG.labelClickPadding / 2}px ${CONFIG.labelClickPadding}px`,
    borderRadius: 6,
    border: `2px solid ${isAnyHovered ? '#3b82f6' : (style?.stroke as string || '#e2e8f0')}`,
    boxShadow: isAnyHovered ? '0 4px 8px rgba(59, 130, 246, 0.3)' : '0 1px 2px rgba(0,0,0,0.1)',
    whiteSpace: 'nowrap',
    zIndex: 1000,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    pointerEvents: 'all',
  }), [labelX, labelY, style?.stroke, isAnyHovered]);

  // Issue #434: Enhanced edge style with hover effects
  // Issue #436: Use combined hover state
  const edgeStyle = useMemo((): CSSProperties => {
    const baseStrokeWidth = (style?.strokeWidth as number) || 2;
    return {
      ...style,
      strokeWidth: isAnyHovered ? baseStrokeWidth + 2 : baseStrokeWidth,
      filter: isAnyHovered ? 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.7))' : undefined,
      transition: 'stroke-width 0.15s ease, filter 0.15s ease',
      cursor: 'pointer',
    };
  }, [style, isAnyHovered]);

  return (
    <>
      {/* Issue #434 & #436: Invisible wider path for easier hover/click detection */}
      <path
        id={`${id}-interaction`}
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={CONFIG.interactionStrokeWidth}
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleEdgeClick}
      />
      {/* Visible edge path */}
      <path
        id={id}
        className="react-flow__edge-path"
        d={edgePath}
        fill="none"
        stroke={edgeStyle.stroke as string || '#b1b1b7'}
        strokeWidth={edgeStyle.strokeWidth as number}
        // #1379 — honor `strokeDasharray` from the converter's edge style
        // so the post-response divider can render as a dashed line.
        // Pre-fix the visible path explicitly named `stroke` + `strokeWidth`
        // but ignored every other style key; the converter's dash hint was
        // silently dropped.
        strokeDasharray={edgeStyle.strokeDasharray as string | undefined}
        style={{
          filter: edgeStyle.filter,
          transition: edgeStyle.transition,
          cursor: 'pointer',
          pointerEvents: 'none', // Let the interaction path handle events
        }}
        markerEnd={markerEnd}
      />
      <EdgeLabelRenderer>
        {/* Sequence number badge - Issue #436: Clickable with hover effects */}
        {showBadge && sequenceText && (
          <div
            style={badgeStyle}
            className="nodrag nopan"
            onClick={handleEdgeClick}
            onMouseEnter={() => setIsLabelHovered(true)}
            onMouseLeave={() => setIsLabelHovered(false)}
          >
            {sequenceText}
          </div>
        )}
        {/* Label text - Issue #436: Clickable with hover effects */}
        {showLabel && labelText && (
          <div
            style={labelContainerStyle}
            className="nodrag nopan"
            onClick={handleEdgeClick}
            onMouseEnter={() => setIsLabelHovered(true)}
            onMouseLeave={() => setIsLabelHovered(false)}
          >
            <span style={labelStyle}>{labelText}</span>
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  );
});

/**
 * Issue #434 & #436: Clickable Smoothstep Edge for Sequential Layout
 * Same as default smoothstep but with wider interaction area for easier clicking
 */
export const ClickableSmoothstepEdge = memo(function ClickableSmoothstepEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
  data,
  label,
  labelStyle,
  labelShowBg,
  labelBgStyle,
  labelBgPadding,
  labelBgBorderRadius,
}: EdgeProps) {
  const { getNodes } = useReactFlow();
  const [isHovered, setIsHovered] = useState(false);
  const [isLabelHovered, setIsLabelHovered] = useState(false);

  // Issue #436: Handle click on edge or label
  const handleEdgeClick = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    const edge = { id, source, target, data, label, style, markerEnd };
    const nodes = getNodes();
    const sourceNode = nodes.find(n => n.id === source);
    const targetNode = nodes.find(n => n.id === target);

    const event = new CustomEvent('edge-label-click', {
      bubbles: true,
      detail: { edge, sourceNode, targetNode }
    });
    (e.target as HTMLElement).dispatchEvent(event);
  }, [id, source, target, data, label, style, markerEnd, getNodes]);

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition: sourcePosition || Position.Bottom,
    targetX,
    targetY,
    targetPosition: targetPosition || Position.Top,
  });

  const isAnyHovered = isHovered || isLabelHovered;
  const baseStrokeWidth = (style?.strokeWidth as number) || 2;
  const strokeWidth = isAnyHovered ? baseStrokeWidth + 1.5 : baseStrokeWidth;

  return (
    <>
      {/* Issue #436: Invisible wider path for easier hover/click detection */}
      <path
        id={`${id}-interaction`}
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={CONFIG.interactionStrokeWidth}
        style={{ cursor: 'pointer' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleEdgeClick}
      />
      {/* Visible edge path */}
      <path
        id={id}
        className="react-flow__edge-path"
        d={edgePath}
        fill="none"
        stroke={(style?.stroke as string) || '#b1b1b7'}
        strokeWidth={strokeWidth}
        // #1379 — honor `strokeDasharray` from the converter's edge style.
        // See SwimlaneCrossColumnEdge above for the rationale.
        strokeDasharray={style?.strokeDasharray as string | undefined}
        style={{
          filter: isAnyHovered ? 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.6))' : undefined,
          transition: 'stroke-width 0.15s ease, filter 0.15s ease',
          cursor: 'pointer',
          pointerEvents: 'none',
        }}
        markerEnd={markerEnd}
      />
      {/* Label if provided - Issue #436: Clickable with hover effects */}
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: isAnyHovered
                ? `translate(-50%, -50%) translate(${labelX}px, ${labelY}px) scale(1.05)`
                : `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              fontSize: 10,
              fontWeight: 500,
              background: isAnyHovered ? '#f0f9ff' : (labelShowBg !== false ? 'white' : 'transparent'),
              padding: Array.isArray(labelBgPadding) ? `${labelBgPadding[0]}px ${labelBgPadding[1]}px` : (labelBgPadding || '4px 8px'),
              borderRadius: labelBgBorderRadius || 4,
              border: isAnyHovered ? '1px solid #3b82f6' : '1px solid transparent',
              boxShadow: isAnyHovered ? '0 2px 4px rgba(59, 130, 246, 0.2)' : undefined,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              pointerEvents: 'all',
              ...labelBgStyle,
            }}
            className="nodrag nopan"
            onClick={handleEdgeClick}
            onMouseEnter={() => setIsLabelHovered(true)}
            onMouseLeave={() => setIsLabelHovered(false)}
          >
            <span style={labelStyle}>{label}</span>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
});

/**
 * Export edge types for registration
 */
export const swimlaneEdgeTypes = {
  swimlaneCrossColumn: SwimlaneCrossColumnEdge,
  clickableSmoothstep: ClickableSmoothstepEdge,
};
